import React, { useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { Mail, Phone, Linkedin, Send, Loader2, CheckCircle, AlertCircle, Instagram, Facebook, Twitter, Youtube } from 'lucide-react';
import emailjs from 'emailjs-com';

const Contact: React.FC = () => {
  const ref = useRef(null);
  const formRef = useRef<HTMLFormElement>(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });

  const [isLoading, setIsLoading] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // EmailJS configuration - Replace with your actual credentials
  const EMAILJS_CONFIG = {
    SERVICE_ID: 'service_r52tmmq',
    TEMPLATE_ID: 'template_ocrtvmc',
    PUBLIC_KEY: 'erSAjCp9YqCqXnioe',
  };

  const info = [
    { icon: Mail, text: 'thingsdigital.in@gmail.com', href: 'mailto:thingsdigital.in@gmail.com' },
    { icon: Phone, text: '+91 7038265251 / 7066190438' },
    
  ];

  // Social Media Links Section
  const socialLinks = [
    { icon: Instagram, name: 'Instagram', href: 'https://www.instagram.com/_thingsdigital_?igsh=MXFzeG82bHoyc2llNA==' },
    { icon: Facebook, name: 'Facebook', href: 'https://www.facebook.com/thingsdigital.in' },
    { icon: Linkedin, name: 'LinkedIn', href: 'https://www.linkedin.com/company/digibyte-media2025/' },
    
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    if (!formRef.current) return;

    const formData = new FormData(formRef.current);
    const data = {
      from_name: formData.get('name'),
      from_email: formData.get('email'),
      message: formData.get('message'),
      to_email: 'thingsdigital.in@gmail.com',
    };

    try {
      await emailjs.send(
        EMAILJS_CONFIG.SERVICE_ID,
        EMAILJS_CONFIG.TEMPLATE_ID,
        data,
        EMAILJS_CONFIG.PUBLIC_KEY
      );

      setIsSent(true);
      formRef.current.reset();
      setTimeout(() => setIsSent(false), 5000);
    } catch (err) {
      console.error('Email sending failed:', err);
      setError('Failed to send message. Please try again or contact us directly.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section id="contact" ref={ref} className="bg-github-darker section-padding">
      <div className="section-container">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6 }}>
          <h2 className="section-title gradient-text">Get In Touch</h2>
          <p className="section-subtitle mb-20">
            Ready to transform your business? Let's discuss your project and bring your vision to life.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-16">
          {/* Contact Info */}
          <motion.div initial={{ opacity: 0, x: -50 }} animate={isInView ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.8, delay: 0.2 }}>
            <h3 className="text-2xl font-bold text-github-text mb-6">Contact Details</h3>
            <div className="space-y-6">
              {info.map((item, i) => (
                <motion.a
                  key={i}
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ x: 5 }}
                  className="flex items-center gap-4 group"
                >
                  <item.icon className="w-6 h-6 text-github-muted group-hover:text-github-accent transition-colors" />
                  <span className="text-github-text group-hover:text-github-accent transition-colors">{item.text}</span>
                </motion.a>
              ))}
            </div>

            {/* Social Media Links */}
            <div className="mt-10">
              <h4 className="text-xl font-semibold text-github-text mb-4">Follow Us</h4>
              <div className="flex gap-5">
                {socialLinks.map((item, i) => (
                  <motion.a
                    key={i}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.2, rotate: 5 }}
                    className="p-3 bg-github-dark border border-github-border rounded-full hover:bg-github-accent/10 transition-all"
                  >
                    <item.icon className="w-5 h-5 text-github-muted hover:text-github-accent" />
                  </motion.a>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            {isSent && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 p-4 bg-green-500/10 border border-green-500/20 rounded-lg flex items-center gap-3"
              >
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span className="text-green-400">Message sent successfully! We'll get back to you soon.</span>
              </motion.div>
            )}

            {error && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-3"
              >
                <AlertCircle className="w-5 h-5 text-red-500" />
                <span className="text-red-400">{error}</span>
              </motion.div>
            )}

            <form ref={formRef} onSubmit={handleSubmit} className="space-y-6">
              <input
                type="text"
                name="name"
                placeholder="Your Name"
                required
                className="w-full p-4 bg-github-dark border border-github-border rounded-lg focus:outline-none focus:ring-2 focus:ring-github-accent text-github-text placeholder-github-muted"
              />
              <input
                type="email"
                name="email"
                placeholder="Your Email"
                required
                className="w-full p-4 bg-github-dark border border-github-border rounded-lg focus:outline-none focus:ring-2 focus:ring-github-accent text-github-text placeholder-github-muted"
              />
              <textarea
                name="message"
                placeholder="Your Message"
                rows={5}
                required
                className="w-full p-4 bg-github-dark border border-github-border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-github-accent text-github-text placeholder-github-muted"
              ></textarea>

              <motion.button
                type="submit"
                disabled={isLoading}
                whileHover={{ scale: isLoading ? 1 : 1.05 }}
                whileTap={{ scale: isLoading ? 1 : 0.98 }}
                className={`w-full flex items-center justify-center gap-2 p-4 font-semibold rounded-lg transition-all duration-200 ${
                  isLoading
                    ? 'bg-github-accent/50 cursor-not-allowed'
                    : 'bg-github-accent hover:bg-github-accent/90'
                } text-white`}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send size={18} />
                    Send Message
                  </>
                )}
              </motion.button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
